import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { InfiniteData } from '@tanstack/react-query';

import { PartnersResponseDTO } from 'services/repositories/partners/partners';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { NetworkRoutes, PartnerDTO } from 'support/types';
import { ConnectionDetailsLocationState } from 'pages/Connections/ConnectionDetailsPage/ConnectionDetailsPage';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { Button } from 'components/Form/Button/Button';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { ListTable } from 'components/ListTable/ListTable';
import { TradePartnerNameAndLogo, TradePartnerNameAndLogoClamping } from 'components/TradePartnerNameAndLogo';

import { NewTradeRequest } from '../NewTradeRequest/NewTradeRequest';
import { OneTimeMeasurer } from './components/OneTimeMeasurer';

const BADGE_COLOR = {
  connected: BadgeColor.green,
  pending: BadgeColor.yellow,
  not_connected: BadgeColor.gray,
};
export function PartnerList({
  data,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  hasPreviousPage,
  onInvite,
}: {
  data: InfiniteData<PartnersResponseDTO> | undefined;
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  hasPreviousPage: boolean | undefined;
  onInvite: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [actionWidth, setActionWidth] = useState<number>(0);

  const tableHeaders = [
    { label: t('partners:list.myPartners.headers.name'), key: 'name' },
    { label: t('partners:list.myPartners.headers.industry'), key: 'industry' },
    { label: t('partners:list.myPartners.headers.status'), key: 'status' },
    { label: '', key: 'actions', alignRight: true },
  ];

  const buildRow = (partner: PartnerDTO) => {
    return [
      {
        element: <TradePartnerNameAndLogo partner={partner} clamping={TradePartnerNameAndLogoClamping.HORIZONTAL} />,
        key: `name ${partner.id}`,
      },
      {
        element: partner.industry ? t(`partners:search.industry.${partner.industry}`) : EMPTY_FIELD,
        key: `industry ${partner.id}`,
      },
      {
        element: (
          <Badge color={BADGE_COLOR[partner.partnerStatus]} type="indicator" className="shrink-0 px-0!">
            {t('partners:list.myPartners.partnerStatus.' + partner.partnerStatus)}
          </Badge>
        ),
        key: `partner status ${partner.id}`,
      },
      {
        element: <PartnerActions partner={partner} width={actionWidth} />,
        key: `actions ${partner.id}`,
        className: '',
      },
    ];
  };

  const listTableData = data?.pages.flatMap(({ items }) => items);
  return (
    <>
      <OneTimeMeasurer contentToMeasure={<NewRequestButton />} onMeasured={(width) => setActionWidth(width)} />
      <ListTable<PartnerDTO>
        headers={tableHeaders}
        data={listTableData}
        isLoading={isLoading}
        rowBuilder={buildRow}
        onRowClick={(partner) =>
          navigate(routeToPage(NetworkRoutes.connectionDetails, { partnerId: partner.id }), {
            state: {
              partner: {
                id: partner.id,
                name: partner.name,
                logoUrl: partner.logoUrl,
                documentTypes: partner.documentTypes,
              },
            } satisfies ConnectionDetailsLocationState,
          })
        }
        pagination={
          <InfiniteScroll
            hasMore={Boolean(hasNextPage)}
            loadMore={fetchNextPage}
            loading={isFetchingNextPage}
            noMoreItemsEnabled={Boolean(hasPreviousPage)}
          />
        }
        emptyState={
          <ListTable.EmptyState background="gray" cols={tableHeaders.length}>
            <div className="flex flex-col items-center gap-6 md:py-6">
              <div className="flex max-w-sm flex-col items-center gap-2">
                <div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-procuros-green-50 text-procuros-green-600">
                  <MagnifyingGlassIcon className="size-6" />
                </div>
                <p className="text-lg font-medium tracking-tight text-gray-900">
                  {t('partners:list.myPartners.emptyState.title')}
                </p>
                <p className="text-sm/relaxed text-gray-500">{t('partners:list.myPartners.emptyState.description')}</p>
              </div>
              <Button
                LeftIcon={UserPlusIcon}
                onClick={onInvite}
                size="extra-large"
                analyticsId="partners:empty_state_invite"
              >
                {t('partners:list.inviteTradePartner')}
              </Button>
            </div>
          </ListTable.EmptyState>
        }
      />
    </>
  );
}

/*
  t('partners:list.myPartners.partnerStatus.connected')
  t('partners:list.myPartners.partnerStatus.pending')
  t('partners:list.myPartners.partnerStatus.not_connected')
*/

const PartnerActions = ({ partner, width }: { partner: PartnerDTO; width: number }) => {
  const { t } = useTranslation();
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const containerProps =
    partner.partnerStatus !== 'not_connected' ? {} : { onClick: (e: React.MouseEvent) => e.stopPropagation() };

  return (
    <div className="flex w-full justify-end" {...containerProps}>
      {partner.partnerStatus !== 'not_connected' ? (
        <div
          className="flex items-center justify-center text-xs font-medium text-gray-700 group-hover:text-procuros-green-600"
          style={{ width: width || 'auto' }}
        >
          {t('partners:list.myPartners.connectedCta')}
        </div>
      ) : (
        <NewRequestButton onClick={() => setShowPopup(true)} />
      )}

      <NewTradeRequest showPopup={showPopup} onClose={() => setShowPopup(false)} partner={partner} connected={false} />
    </div>
  );
};

const NewRequestButton = ({ onClick, ref }: { onClick?: () => void; ref?: React.Ref<HTMLButtonElement> }) => {
  const { t } = useTranslation();
  return (
    <Button ref={ref} size="small" variant="secondary" onClick={onClick} analyticsId="partners:new_trade_request">
      {t('partners:connectionDetails.noConnection.cta')}
    </Button>
  );
};
